import * as React from "react";
import { useCallback } from "react";
import { ScrollView, View } from "react-native";
import { cn } from "@/shared/lib/utils";
import { Constants, type Enums } from "@lezz/supabase";
import { BottomSheet } from "@/shared/components/ui/bottom-sheet";
import { Button } from "@/shared/components/ui/button";
import { Heading } from "@/shared/components/ui/heading";
import { Pill } from "@/shared/components/ui/pill";

type TagValue = Enums<"event_tag"> | Enums<"venue_tag"> | Enums<"music_tag">;

type FilterSection = {
  title: string;
  tags: readonly TagValue[];
};

const FILTER_SECTIONS: FilterSection[] = [
  {
    title: "Tipo de evento",
    tags: Constants.public.Enums.event_tag,
  },
  {
    title: "Estabelecimentos",
    tags: Constants.public.Enums.venue_tag,
  },
  {
    title: "Música",
    tags: Constants.public.Enums.music_tag,
  },
];

type FilterModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedTags: TagValue[];
  onTagsChange: (tags: TagValue[]) => void;
};

export function FilterModal({
  open,
  onOpenChange,
  selectedTags,
  onTagsChange,
}: FilterModalProps) {
  const handleTagToggle = useCallback(
    (tag: TagValue) => {
      if (selectedTags.includes(tag)) {
        onTagsChange(selectedTags.filter((t) => t !== tag));
      } else {
        onTagsChange([...selectedTags, tag]);
      }
    },
    [selectedTags, onTagsChange]
  );

  const handleClear = useCallback(() => {
    onTagsChange([]);
  }, [onTagsChange]);

  const handleApply = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <BottomSheet
      visible={open}
      onClose={() => onOpenChange(false)}
      contentClassName="flex-1"
    >
      <ScrollView
        showsVerticalScrollIndicator={false}
        bounces={false}
        className="shrink"
      >
        {FILTER_SECTIONS.map((section, sectionIndex) => (
          <View
            key={section.title}
            className={cn(
              "px-5 py-6",
              sectionIndex > 0 && "border-border border-t"
            )}
          >
            <Heading variant="section" className="mb-4">
              {section.title}
            </Heading>

            <View className="flex-row flex-wrap gap-2">
              {section.tags.map((tag) => {
                const isSelected = selectedTags.includes(tag);
                return (
                  <Pill
                    key={tag}
                    label={tag}
                    variant="outline"
                    tone="brand"
                    selected={isSelected}
                    onPress={() => handleTagToggle(tag)}
                    accessibilityLabel={`${tag} filter`}
                  />
                );
              })}
            </View>
          </View>
        ))}
      </ScrollView>

      <View className="border-border flex-row gap-3 border-t px-5 pt-4">
        <Button
          variant="outline"
          className="flex-1"
          onPress={handleClear}
          accessibilityLabel="Clear all filters"
        >
          Limpar
        </Button>
        <Button
          className="flex-1"
          onPress={handleApply}
          accessibilityLabel="Apply filters and show events"
        >
          Filtrar
        </Button>
      </View>
    </BottomSheet>
  );
}
